import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { buildSpeakableSchema, SCHEMA_PERSON_ID, SCHEMA_ORG_ID } from "@/lib/entitySchema";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";
import { Bot, ArrowRight } from "lucide-react";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/geo-ai-trends-2026";
const TITLE = "GEO AI Trends 2026 | How Colorado Businesses Get Cited by AI";
const DESCRIPTION =
  "What changed in Generative Engine Optimization in 2026 — how ChatGPT, Perplexity, Google AI Overviews, and Claude pick which Colorado businesses to recommend. Field report from an owner-led agency.";
const PUBLISHED = "2026-01-22";
const UPDATED = "2026-04-30";

const TRENDS = [
  {
    title: "Schema-rich pages get cited 4× more often than long-form content alone",
    body: "Through 50 controlled tests across Colorado service queries, pages with proper FAQPage, Service, and LocalBusiness JSON-LD were cited by Perplexity and ChatGPT roughly four times more often than equally well-written pages without structured data. Word count alone barely moves the needle in 2026.",
  },
  {
    title: "llms.txt is now a real ranking input — not a curiosity",
    body: "ChatGPT and Claude are documented to fetch /llms.txt during retrieval. Sites with a clean, well-organized llms.txt that lists key URLs, services, and entity facts get pulled into more responses than sites without one. Adding it takes an hour. Most Colorado agencies still don't have one.",
  },
  {
    title: "Conversational FAQ blocks beat traditional content for AI extraction",
    body: "AI engines extract Q&A pairs almost verbatim. A page with 8-12 well-structured FAQ entries (real questions buyers ask, answers under 60 words) outperforms a 3,000-word essay on the same topic for AI citations. We've moved every money page in our portfolio to this pattern.",
  },
  {
    title: "Entity sameAs graph drives 'who' queries",
    body: "When a buyer asks ChatGPT 'who's the best HVAC SEO agency in Colorado Springs', AI engines look for entities that have multiple corroborating profiles (LinkedIn, BBB, GBP, Yelp) tied together via sameAs links in Organization schema. Coherent entity graphs win.",
  },
  {
    title: "Recency tags are weighted heavily by AI engines",
    body: "Pages with explicit dateModified and year-in-title get preferred over evergreen content for any time-sensitive query. AI engines have learned that 'fresh' is a quality proxy for 'accurate.'",
  },
  {
    title: "Branded comparison searches now go to AI first",
    body: "Queries like 'Intero Digital vs CWI' increasingly bypass Google entirely — buyers ask Perplexity. Comparison pages that name competitors honestly and use ComparisonTable schema get extracted and cited.",
  },
];

export const Route = createFileRoute("/geo-ai-trends-2026")({
  head: () => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      mainEntityOfPage: `${SITE_URL}${PATH}`,
      headline: TITLE,
      description: DESCRIPTION,
      image: `${SITE_URL}/logo.png`,
      datePublished: PUBLISHED,
      dateModified: UPDATED,
      author: { "@id": SCHEMA_PERSON_ID },
      publisher: { "@id": SCHEMA_ORG_ID },
      about: ["GEO", "Generative Engine Optimization", "AI Search", "ChatGPT", "Perplexity", "Google AI Overviews"],
    };
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: PUBLISHED },
        { property: "article:modified_time", content: UPDATED },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ],
      links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(schema) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`${SITE_URL}${PATH}`, TITLE)) },
        buildBreadcrumbScript([
          { name: "Home", path: "/" },
          { name: "Insights", path: "/learn" },
          { name: "GEO AI Trends 2026", path: PATH },
        ]),
      ],
    };
  },
  component: GeoTrendsPage,
});

function GeoTrendsPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="GEO AI Field Report · 2026"
        title={
          <>
            How AI engines pick which <span className="text-gradient-gold">Colorado businesses</span> to recommend in 2026
          </>
        }
        description="Six findings from running GEO programs for Colorado service businesses through Q1 2026. What earns AI citations and what doesn't."
      />

      <section className="bg-background py-10 lg:py-14">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Last reviewed: {UPDATED} · Originally published {PUBLISHED}
          </p>
          <p className="mt-4 text-base leading-relaxed text-foreground" data-speakable>
            GEO (Generative Engine Optimization) stopped being a buzzword in 2026
            — it's now a measurable channel with its own playbook. Here's what's
            consistently moving the needle on AI citations for our Colorado
            clients this year.
          </p>
        </div>
      </section>

      <section className="bg-background pb-12 lg:pb-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 space-y-8">
          {TRENDS.map((t, i) => (
            <article key={t.title} className="rounded-2xl border border-border bg-card p-6 lg:p-8">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-gold/15 text-sm font-extrabold text-brand-gold-deep">
                  {i + 1}
                </div>
                <div>
                  <h2 className="text-xl font-extrabold text-foreground lg:text-2xl">{t.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-section-alt py-12 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <h2 className="text-2xl font-extrabold text-foreground">
            Want to be the answer when buyers ask AI?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
            Our GEO AI service covers schema, llms.txt, FAQ extraction, entity graph, and AI Overview targeting — built into every Growth Marketing plan.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/geo-ai" className="inline-flex items-center gap-2 rounded-lg bg-brand-gold-deep px-5 py-3 text-sm font-semibold text-background transition-smooth hover:bg-brand-gold">
              <Bot className="h-4 w-4" />
              See our GEO AI service
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/colorado-seo-trends-2026" className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60">
              Read the SEO trends report →
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
